"use client"

import { Global, css } from "@emotion/react"
import styled from "@emotion/styled"
// import { useLayoutEffect } from "react"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useUser } from "contexts/User"
import { useViewport } from "contexts/Viewport"
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Helmet } from "react-helmet-async"

import Button from "components/button/Button"
import useScript from "hooks/useScript"

const version = "4.0.1"
const closedCaptionLocaleCodes = new Set(["en", "en-us"])

const playerStyles = css`
  .video-js.cld-video-player .vjs-fullscreen-control,
  .cld-video-player .vjs-fullscreen-control {
    visibility: hidden;
    pointer-events: none;
  }
`

const Player = styled.video`
  position: fixed !important;
  z-index: 11000;
  pointer-events: all;
  cursor: pointer;
  top: 0;

  [dir="ltr"] & {
    left: -9999px;
  }

  [dir="rtl"] & {
    right: -9999px;
  }
`

const CloseButtonPosition = styled.div`
  position: absolute;
  inset-block-start: calc(env(safe-area-inset-top, 0px) + 2rem);
  z-index: 20;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;

  inset-inline-end: calc(env(safe-area-inset-right, 0px) + 1.5rem);

  [dir="rtl"] & {
    inset-inline-end: calc(env(safe-area-inset-left, 0px) + 1.5rem);
  }

  .video-js.vjs-user-active &:not([inert]),
  .video-js.vjs-paused &:not([inert]) {
    opacity: 1;
    pointer-events: auto;
  }

  .video-js.vjs-user-inactive.vjs-playing &:not([inert]) {
    opacity: 0;
    pointer-events: none;
  }
`

const CloseButton = styled(Button)`
  pointer-events: all;
  cursor: pointer;
`

const getVideoDevice = video => {
  const v7Type = Array.isArray(video?.metadata?.v7_type) ? video.metadata.v7_type[0] : video?.metadata?.v7_type

  if (v7Type === "portrait") return "portrait"

  return video?.width / video?.height < 1 ? "portrait" : "landscape"
}

function CoreVideo(
  {
    sources: sourcesNonLocalizable,
    sources_localized: sourcesLocalized,
    quality,
    no_captions = false,
    has_closed_captions = false,
    onChange,
    onClose,
    adaptive_bitrate,
    key,
    aria_hidden,
  },
  handle
) {
  const locale = useLocale()
  const status = useScript(`https://cdn.jsdelivr.net/npm/cloudinary-video-player@${version}/dist/cld-video-player.min.js`)
  const { orientation, isMobile: isMobileViewport } = useViewport()
  const { isMobile } = useUser()
  const rvideo = useRef()
  const rvolumebar = useRef()
  const rprogressholder = useRef()
  const rbuttons = useRef()
  const env = useEnv()
  const rplayer = useRef()
  const rsourceKey = useRef()
  const rclose = useRef(onClose)
  const rclosePosition = useRef()

  const sources = sourcesLocalized?.length ? sourcesLocalized : sourcesNonLocalizable

  const { cloudinary: { cloudName, secureDistribution } = {} } = env

  const [player, setPlayer] = useState()
  const [playerElement, setPlayerElement] = useState()
  // const [isIOS, setIsIOS] = useState(false)
  // const [isAndroid, setIsAndroid] = useState(false)
  // const [iOSVersion, setIOSVersion] = useState(null)

  // useLayoutEffect(() => {
  //   const ua = navigator?.userAgent || ""
  //   const isAndroid = ua.toLowerCase().includes("android")
  //   const iosMatch = ua.match(/OS (\d+)[._](\d+)?(?:[._](\d+))?/)

  //   const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
  //   setIsAndroid(isAndroid)
  //   setIsIOS(isIOS)

  //   if (isIOS && iosMatch) {
  //     const major = Number.parseInt(iosMatch[1], 10)
  //     const minor = Number.parseInt(iosMatch[2] || "0", 10)
  //     const patch = Number.parseInt(iosMatch[3] || "0", 10)
  //     setIOSVersion({ major, minor, patch })
  //   }
  // }, [])

  useEffect(() => {
    rclose.current = onClose
  }, [onClose])

  useEffect(() => {
    if (status !== "ready" || rplayer.current) return

    const config = {
      cloud_name: cloudName,
      secure: !!secureDistribution,
      private_cdn: !!secureDistribution,
      cname: !!secureDistribution && secureDistribution,
      secure_distribution: !!secureDistribution && secureDistribution,
    }

    const adaptiveBitrateOptions = adaptive_bitrate
      ? {
          transformation: [
            {
              streaming_profile: "auto:maxres_2160p",
            },
          ],
          sourceTypes: ["hls", "webm/vp9", "mp4/h265", "mp4"],
        }
      : {
          transformation: {
            quality: quality ?? "auto:eco",
            crop: "limit",
            width: isMobileViewport.get() ? 1080 : 2160,
          },
          sourceTypes: ["webm/vp9", "mp4/h265", "mp4"],
        }

    const player = global.cloudinary.videoPlayer(rvideo.current, {
      ...config,
      controls: true,
      colors: { base: "rgb(var(--green))", accent: "rgb(var(--green))", text: "#fff" },
      hideContextMenu: true,
      showLogo: false,
      allowUsageReport: false,
      cloudinaryAnalytics: false,
      disablePictureInPicture: true,
      disableRemotePlayback: true,
      playsInline: true, // iOS
      playedEventPercents: [50, 100],
      seekThumbnails: false,
      posterOptions: {
        publicId: "",
        transformation: {
          startOffset: "0",
        },
      },
      preload: "none",
      ...adaptiveBitrateOptions,
      ...(isMobile && {
        html5: {
          nativeTextTracks: true,
          featuresNativeTextTracks: false,
        },
      }),
    })
    rplayer.current = player
    const playerRoot = player.el?.()
    setPlayer(player)
    setPlayerElement(playerRoot)

    const videoName = sources[0]?.public_id?.replace(/-portrait$/, "")

    /* Maximize player during playback */
    player.on("play", () => {
      setTimeout(() => {
        player.maximize()
      }, 300)
    })

    /* Exit fullscreen when the end is reached */
    player.on("ended", () => {
      player.exitMaximize()
    })

    player.on("loadstart", () => {
      const hls = player.videojs.tech({ IWillNotUseThisInPlugins: true })?.sourceHandler_?.hls

      if (hls) {
        hls.startLevel = 3
      }
    })

    player.on("fullscreenchange", () => {
      /* Stop the video if fullscreen is exited */
      const maximized = player.isMaximized()

      if (rclosePosition.current) rclosePosition.current.inert = !maximized

      if (!maximized) {
        player.stop?.()
        rclose.current?.()
      }
    })

    /* WA */
    player.on("percentsplayed", ({ eventData: { percent } }) => {
      waTriggerEvent({
        eventName: `video${percent}`,
        eventType: "Click",
        videoInfo: {
          name: videoName,
        },
      })
    })

    player.on("seek", ({ eventData: { seekStart, seekEnd } }) => {
      const duration = player.duration()

      if (seekStart < duration * 0.5 && seekEnd >= duration * 0.5) {
        waTriggerEvent({
          eventName: "video50",
          eventType: "Click",
          videoInfo: {
            name: videoName,
          },
        })
      }
    })

    player.on("fullscreenchange", e => {
      disableButtons(!player.isMaximized())
    })

    rbuttons.current = rvideo.current.parentNode?.querySelectorAll("button")
    rvolumebar.current = rvideo.current.parentNode?.getElementsByClassName("vjs-volume-bar")[0]
    rprogressholder.current = rvideo.current.parentNode?.getElementsByClassName("vjs-progress-holder")[0]

    disableButtons(!player.isMaximized())

    return () => {
      if (rplayer.current) {
        rplayer.current.dispose?.()
        rplayer.current.destroy?.()
      }
      rplayer.current = null
      rsourceKey.current = null
      setPlayerElement(undefined)
      setPlayer(undefined)
    }
  }, [status, adaptive_bitrate, cloudName, quality, secureDistribution, isMobileViewport, isMobile, sources])

  const disableButtons = state => {
    rvolumebar.current?.setAttribute("tabindex", state ? -1 : 0)
    rprogressholder.current?.setAttribute("tabindex", state ? -1 : 0)

    if (rbuttons.current) {
      for (const bt of rbuttons.current) {
        bt.disabled = state
      }
    }
  }

  const loadAndPlay = () => {
    if (!rplayer.current) return

    const videos = sources.reduce((acc, v) => {
      acc[getVideoDevice(v)] = v
      return acc
    }, {})

    const source = sources.length > 1 ? { portrait: videos.portrait?.public_id, landscape: videos.landscape?.public_id } : sources[0].public_id
    const videoName = sources[0]?.public_id?.replace(/-portrait$/, "")
    const localeCode = locale.current.codes.ws
    const closedCaptionLocaleCode = localeCode.toLowerCase()
    const closedCaptionFileLocaleCode = closedCaptionLocaleCode === "en-us" ? "en" : closedCaptionLocaleCode
    const hasSupportedClosedCaptions = has_closed_captions && closedCaptionLocaleCodes.has(closedCaptionLocaleCode)

    // function setFontSize(videos) {
    //   if (isMobile.get()) {
    //     if (isIOS) {
    //       if (iOSVersion?.major === 18 && iOSVersion?.minor === 5) {
    //         return videos.portrait ? "1rem" : "1.1rem" // Custom for iOS 18.5
    //       }
    //       return videos.portrait ? "1.2rem" : "1.5rem" // Default iOS font sizes
    //     }
    //     if (isAndroid) {
    //       return videos.portrait ? "1.3rem" : "1.5rem" // Android font sizes
    //     }
    //   } else {
    //     return "2rem" // Default desktop font size
    //   }
    // }

    const textTracks = {
      ...(hasSupportedClosedCaptions && {
        captions: {
          label: locale.current.language,
          language: closedCaptionLocaleCode,
          default: true,
          url: `https://${secureDistribution}/raw/upload/${videoName}_captions_${closedCaptionFileLocaleCode}.vtt`,
        },
      }),
      ...(!no_captions && {
        subtitles: [
          {
            label: locale.current.language,
            language: localeCode,
            default: !hasSupportedClosedCaptions,
            url: `https://${secureDistribution}/raw/upload/${videoName}_${localeCode}.vtt`,
          },
        ],
      }),
      // options: {
      //   fontSize: setFontSize(videos),
      // },
    }

    const selectedSource = sources.length > 1 ? source[orientation.get()] : source
    const sourceOptions = {
      textTracks,
      ...(env.withCredentials && {
        withCredentials: true,
      }),
      adaptiveStreaming: {
        strategy: "highQuality",
      },
    }
    const sourceKey = JSON.stringify({ selectedSource, sourceOptions })

    if (rsourceKey.current === sourceKey) {
      rplayer.current.currentTime?.(0)
    } else {
      rplayer.current.source(selectedSource, sourceOptions)
      rsourceKey.current = sourceKey
    }

    rplayer.current.one?.("playing", () => {
      waTriggerEvent({
        eventName: "video0",
        eventType: "Click",
        videoInfo: {
          name: videoName,
        },
      })
    })

    rplayer?.current?.play?.()
  }

  const closePlayer = e => {
    e?.preventDefault?.()
    e?.stopPropagation?.()

    const currentPlayer = rplayer.current

    if (currentPlayer?.isMaximized?.()) {
      currentPlayer.exitMaximize?.()
      return
    }

    currentPlayer?.stop?.()
    rclose.current?.()
  }

  const setClosePosition = element => {
    rclosePosition.current = element

    if (element) element.inert = !rplayer.current?.isMaximized?.()
  }

  useImperativeHandle(handle, () => ({
    ...player,
    play: loadAndPlay,
  }))

  // console.verbose("VideoPlayerCLD(%o)", { textTracks })
  return (
    <>
      <Global styles={playerStyles} />
      <Helmet onChangeClientState={newState => onChange?.(newState)}>
        <link rel='preconnect' href='https://cdn.jsdelivr.net' crossorigin />
        <link rel='dns-prefetch' href='https://cdn.jsdelivr.net' />
        <link rel='stylesheet' href={`https://cdn.jsdelivr.net/npm/cloudinary-video-player@${version}/dist/cld-video-player.min.css`} />
      </Helmet>
      <div aria-hidden={aria_hidden} key={key} id={key} style={{ display: "contents" }}>
        <Player width='1080' className='cld-video-player' ref={rvideo} crossOrigin={env.withCredentials ? "use-credentials" : "anonymous"} />
      </div>
      {playerElement &&
        createPortal(
          <CloseButtonPosition ref={setClosePosition}>
            <CloseButton className='icon translucent-dark dark-theme' icon='close' aria_label='Close video' onPress={closePlayer} />
          </CloseButtonPosition>,
          playerElement
        )}
    </>
  )
}

export default forwardRef(CoreVideo)
